import { useState } from "react";
import { WE_PEDIDOS_LOGO_URL as mozzoIcon } from "../../services/local/institutionalBranding";
import {
  isUsingLocalWeCustomerAuth,
  requestWePasswordReset,
  signInWeCustomer,
  signUpWeCustomer,
} from "../../services/supabase/weCustomerAuthService";
import WePedidosAndroidLauncher from "./WePedidosAndroidLauncher";

const initialRegister = {
  fullName: "",
  email: "",
  phone: "",
  password: "",
  confirmation: "",
  acceptedTerms: false,
};

export default function WeCustomerAccess({ onAuthenticated }) {
  const [mode, setMode] = useState("login");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [register, setRegister] = useState(initialRegister);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const callbackUrl = `${window.location.origin}/we-pedidos/auth/callback`;

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await signInWeCustomer(login);
      onAuthenticated();
    } catch (requestError) {
      setError(requestError?.message || "No pudimos iniciar sesión.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (register.password !== register.confirmation) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!register.acceptedTerms) {
      setError("Debes aceptar los términos y la política de privacidad.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signUpWeCustomer({
        fullName: register.fullName,
        email: register.email,
        phone: register.phone,
        password: register.password,
        redirectTo: callbackUrl,
      });
      if (result.requiresEmailConfirmation) {
        setMessage(
          "Te enviamos un correo para confirmar la cuenta. Ábrelo y volverás a We! pedidos."
        );
      } else {
        onAuthenticated();
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos crear tu cuenta.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    const email = login.email.trim();
    if (!email) {
      setError("Primero ingresa el correo de tu cuenta.");
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const result = await requestWePasswordReset({
        email,
        redirectTo: `${callbackUrl}?mode=recovery`,
      });
      setMessage(
        result.localPreview
          ? "En la vista local la cuenta está disponible. Usa la contraseña con la que la creaste."
          : "Te enviamos un correo para recuperar la contraseña."
      );
    } catch (requestError) {
      setError(requestError?.message || "No pudimos enviar el correo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="we-access-shell">
      <section className="we-access-brand" aria-label="We! pedidos">
        <img className="we-logo-image" src={mozzoIcon} alt="" />
        <div>
          <span>Delivery en un solo lugar</span>
          <h1><strong>We!</strong> pedidos</h1>
          <p>
            Una cuenta para pedir en todos los restaurantes, guardar tus
            direcciones y seguir cada entrega.
          </p>
        </div>
      </section>

      <section className="we-access-panel" aria-labelledby="we-access-title">
        <div className="we-access-mobile-brand">
          <img className="we-logo-image" src={mozzoIcon} alt="" />
          <strong>We! pedidos</strong>
        </div>
        <p className="we-eyebrow">Tu cuenta personal</p>
        <h2 id="we-access-title">
          {mode === "login" ? "Ingresá para pedir" : "Creá tu cuenta"}
        </h2>
        <p className="we-access-copy">
          {mode === "login"
            ? "Recuperá tus datos y direcciones en cualquier restaurante."
            : "Registrate una sola vez y usá la misma cuenta en toda la app."}
        </p>

        <div className="we-access-tabs" role="tablist" aria-label="Acceso a We! pedidos">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={mode === "login" ? "is-active" : ""}
            onClick={() => chooseMode("login")}
          >
            Ingresar
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={mode === "register" ? "is-active" : ""}
            onClick={() => chooseMode("register")}
          >
            Crear cuenta
          </button>
        </div>

        {mode === "login" ? (
          <form className="we-access-form" onSubmit={handleLogin}>
            <label>
              Correo electrónico
              <input
                type="email"
                autoComplete="email"
                value={login.email}
                onChange={(event) => setLogin({ ...login, email: event.target.value })}
                placeholder="tu@correo.com"
                required
              />
            </label>
            <label>
              Contraseña
              <span className="we-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={login.password}
                  onChange={(event) => setLogin({ ...login, password: event.target.value })}
                  placeholder="Tu contraseña"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? "Ocultar" : "Ver"}
                </button>
              </span>
            </label>
            <button
              type="button"
              className="we-access-forgot"
              onClick={handlePasswordReset}
              disabled={submitting}
            >
              Olvidé mi contraseña
            </button>
            <button className="we-primary-button" type="submit" disabled={submitting}>
              {submitting ? "Ingresando..." : "Ingresar a We! pedidos"}
            </button>
          </form>
        ) : (
          <form className="we-access-form" onSubmit={handleRegister}>
            <label>
              Nombre y apellido
              <input
                autoComplete="name"
                value={register.fullName}
                onChange={(event) => setRegister({ ...register, fullName: event.target.value })}
                placeholder="Ej. Diego Portillo"
                required
              />
            </label>
            <label>
              Correo electrónico
              <input
                type="email"
                autoComplete="email"
                value={register.email}
                onChange={(event) => setRegister({ ...register, email: event.target.value })}
                placeholder="tu@correo.com"
                required
              />
            </label>
            <label>
              Celular
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={register.phone}
                onChange={(event) => setRegister({ ...register, phone: event.target.value })}
                placeholder="Ej. 379 4123456"
                required
              />
            </label>
            <div className="we-access-form-row">
              <label>
                Contraseña
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={register.password}
                  onChange={(event) => setRegister({ ...register, password: event.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </label>
              <label>
                Repetir contraseña
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={register.confirmation}
                  onChange={(event) => setRegister({ ...register, confirmation: event.target.value })}
                  placeholder="Repetila"
                  required
                />
              </label>
            </div>
            <label className="we-access-terms">
              <input
                type="checkbox"
                checked={register.acceptedTerms}
                onChange={(event) => setRegister({ ...register, acceptedTerms: event.target.checked })}
              />
              <span>Acepto los términos de uso y la política de privacidad.</span>
            </label>
            <button
              type="button"
              className="we-access-password-toggle"
              onClick={() => setShowPassword((current) => !current)}
            >
              {showPassword ? "Ocultar contraseñas" : "Mostrar contraseñas"}
            </button>
            <button className="we-primary-button" type="submit" disabled={submitting}>
              {submitting ? "Creando cuenta..." : "Crear cuenta"}
            </button>
          </form>
        )}

        {error ? <p className="we-access-message is-error" role="alert">{error}</p> : null}
        {message ? <p className="we-access-message is-success" role="status">{message}</p> : null}
        {isUsingLocalWeCustomerAuth() ? (
          <p className="we-access-preview-note">
            Vista local: el registro queda guardado solamente en este navegador.
          </p>
        ) : null}
        <WePedidosAndroidLauncher />
      </section>
    </main>
  );
}
